import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  MessageSquare, CheckCircle2, AlertTriangle, Info, Send, Loader2, ExternalLink,
} from "lucide-react";

/** Admin-only panel: shows Twilio SMS wiring state, an India TRAI DLT
 * compliance hint, and a small test-send form so the admin can verify
 * end-to-end delivery the moment they add real credentials. */
export function SmsStatusPanel() {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState("");
  const [body, setBody] = useState("Manodayam test SMS — you're all set.");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/sms/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const send = async () => {
    if (!to.trim()) {
      toast.error("Enter a phone number in E.164 format, e.g. +919876543210");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/sms/test", { to, body });
      if (r.data.live) {
        toast.success(`Live SMS sent to ${r.data.to}. Check the phone.`);
      } else {
        toast.success(`Mock SMS accepted for ${r.data.to} (Twilio keys not set — log-only).`);
      }
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSending(false);
    }
  };

  const live = !!status?.live;

  return (
    <section
      className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-5 lg:p-6 space-y-4"
      data-testid="admin-sms-panel"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)] flex items-center justify-center shrink-0">
            <MessageSquare size={18} />
          </div>
          <div>
            <div className="uppercase-label">SMS reminders (Twilio)</div>
            <h3 className="font-display text-lg mt-0.5">Delivery status &amp; compliance</h3>
          </div>
        </div>
        <StatusPill live={live} status={status} />
      </div>

      {/* Wiring rows */}
      {status && (
        <div className="grid sm:grid-cols-3 gap-2 text-xs" data-testid="sms-wiring">
          <WireRow label="Account SID" ok={status.sid_set} />
          <WireRow label="Auth Token" ok={status.token_set} />
          <WireRow label="Sender (From / MG)" ok={status.from_set} />
        </div>
      )}

      {/* India DLT compliance note */}
      <div
        className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 p-4 flex gap-3"
        data-testid="dlt-compliance-note"
      >
        <AlertTriangle size={18} className="shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed">
          <div className="font-semibold">India TRAI DLT registration required for +91 numbers</div>
          <p className="mt-1">
            SMS to Indian mobile numbers <b>will fail</b> unless the sender ID and every message
            template are registered on a TRAI-approved DLT platform (Jio, Airtel, Vi, BSNL).
            Twilio's international long-code and toll-free routes are blocked for +91 by TRAI.
          </p>
          <ul className="mt-2 space-y-1 list-disc list-inside">
            <li>Register your entity + Header + Template on a DLT portal (usually 24–48h).</li>
            <li>Use a Messaging Service SID (<code>MG…</code>) with a DLT-registered Indian sender.</li>
            <li>Message content must exactly match the registered template — even punctuation.</li>
            <li>Trial Twilio accounts also cannot send to unverified numbers.</li>
          </ul>
          <a
            href="https://www.twilio.com/docs/messaging/regulatory/india"
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1 font-semibold underline"
            data-testid="dlt-docs-link"
          >
            Twilio: India regulatory guide <ExternalLink size={12} />
          </a>
        </div>
      </div>

      {/* Live-mode hint OR setup hint */}
      {!live && (
        <div
          className="rounded-xl bg-[color:var(--mano-primary)]/5 border border-[color:var(--mano-primary)]/20 text-[color:var(--mano-ink)] p-4 flex gap-3"
          data-testid="sms-setup-hint"
        >
          <Info size={18} className="shrink-0 mt-0.5 text-[color:var(--mano-primary)]" />
          <div className="text-sm leading-relaxed">
            <b>Not live yet.</b> Add these three variables to <code>backend/.env</code> and restart the
            backend to activate real SMS reminders:
            <pre className="mt-2 bg-white border border-[color:var(--mano-border)] rounded-lg p-3 text-xs overflow-x-auto">{`TWILIO_ACCOUNT_SID=AC...
TWILIO_AUTH_TOKEN=...
TWILIO_FROM_NUMBER=+15551234567     # or MG... for a Messaging Service`}</pre>
          </div>
        </div>
      )}

      {/* Test-send form */}
      <div>
        <div className="uppercase-label">Send test SMS</div>
        <div className="mt-2 grid sm:grid-cols-[1fr_2fr_auto] gap-2">
          <input
            data-testid="sms-test-to"
            type="tel"
            inputMode="tel"
            placeholder="+919876543210"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-11 px-3 rounded-lg border border-[color:var(--mano-border)] bg-white focus:outline-none focus:border-[color:var(--mano-primary)]"
          />
          <input
            data-testid="sms-test-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="h-11 px-3 rounded-lg border border-[color:var(--mano-border)] bg-white focus:outline-none focus:border-[color:var(--mano-primary)]"
          />
          <button
            data-testid="sms-test-send"
            onClick={send}
            disabled={sending}
            className="h-11 px-4 rounded-lg bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
        <div className="text-[11px] text-[color:var(--mano-ink-soft)] mt-2">
          Non-Indian numbers work as soon as keys are set. +91 numbers additionally require DLT
          registration (see above).
        </div>
      </div>
    </section>
  );
}

function WireRow({ label, ok }) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 flex items-center justify-between ${
        ok
          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
          : "bg-slate-50 border-slate-200 text-slate-600"
      }`}
      data-testid={`wire-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      <span className="font-medium">{label}</span>
      <span className="inline-flex items-center gap-1">
        {ok ? <CheckCircle2 size={13} /> : <span className="text-slate-400">…</span>}
        {ok ? "set" : "missing"}
      </span>
    </div>
  );
}

function StatusPill({ live, status }) {
  if (!status) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
        live ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
      }`}
      data-testid="sms-status-pill"
    >
      <span className={`h-2 w-2 rounded-full ${live ? "bg-emerald-500" : "bg-amber-500"}`} />
      {live ? "Live" : "Log-only (mock)"}
    </span>
  );
}
